import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { Plus, Pencil, Power, PowerOff, Trash2, Link2, Unlink } from "lucide-react";

const TYPE_OPTIONS = [
  "single_line", "multi_line", "number", "decimal", "currency", "percent",
  "date", "checkbox", "dropdown", "multi_select", "url", "email", "phone",
  "computed",
];

/**
 * v5.7 Addition 2 — Field Library screen.
 * Lets VC Admin list all fields (core + custom) visible in their tenant,
 * create custom fields at tenant or pipeline scope, rename them, deactivate/
 * reactivate, delete (when usage_count = 0), and attach/detach from the
 * currently selected pipeline.
 */
export default function FieldLibrary() {
  const { pipelineId, pipelines } = usePipelineCtx();
  const [module, setModule] = useState("deal");
  const [fields, setFields] = useState([]);
  const [attachments, setAttachments] = useState([]); // for current pipeline+module
  const [showInactive, setShowInactive] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [renameFor, setRenameFor] = useState(null);

  const load = async () => {
    if (!pipelineId) return;
    const { data: allFields } = await api.get("/fields", {
      params: { module, include_inactive: showInactive, pipeline_id: pipelineId },
    });
    setFields(allFields);
    const { data: atts } = await api.get("/fields/attachments", {
      params: { pipeline_id: pipelineId, module },
    });
    setAttachments(atts);
  };
  useEffect(() => { load(); }, [module, showInactive, pipelineId]); // eslint-disable-line

  const isAttached = (fid) => attachments.some((a) => a.field_definition_id === fid);
  const attachmentIdFor = (fid) =>
    attachments.find((a) => a.field_definition_id === fid)?.attachment_id;

  const attach = async (fid) => {
    await api.post("/fields/attachments", { field_definition_id: fid, pipeline_id: pipelineId });
    toast.success("Field attached");
    load();
  };
  const detach = async (fid) => {
    const aid = attachmentIdFor(fid);
    if (!aid) return;
    await api.delete(`/fields/attachments/${aid}`);
    toast.success("Field detached");
    load();
  };

  const deactivate = async (f) => {
    if (!window.confirm(`Deactivate "${f.display_label}"? Records already holding a value will keep showing it, read-only.`))
      return;
    try {
      await api.post(`/fields/${f.field_definition_id}/deactivate`);
      toast.success("Field deactivated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const reactivate = async (f) => {
    await api.post(`/fields/${f.field_definition_id}/reactivate`);
    toast.success("Field reactivated");
    load();
  };
  const del = async (f) => {
    if (f.usage_count > 0) {
      toast.error(`Field holds values on ${f.usage_count} records. Deactivate it instead.`);
      return;
    }
    if (!window.confirm(`Delete "${f.display_label}" permanently? This cannot be undone.`))
      return;
    try {
      await api.delete(`/fields/${f.field_definition_id}`);
      toast.success("Field deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const currentPipe = pipelines.find((p) => p.pipeline_id === pipelineId);
  const filtered = showInactive ? fields : fields.filter((f) => f.is_active !== false);

  return (
    <div className="space-y-4">
      <div className="cw-card p-5">
        <div className="text-xs text-[var(--cw-muted)]">
          Field Library for pipeline <strong>{currentPipe?.pipeline_name || "…"}</strong>.
          Change the pipeline via the sidebar switcher.
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-md border p-0.5" style={{ borderColor: "var(--cw-border)" }}>
            {["deal", "founder"].map((m) => (
              <button
                key={m}
                onClick={() => setModule(m)}
                className="px-3 py-1.5 text-xs font-medium rounded transition capitalize"
                style={
                  module === m
                    ? { background: "var(--cw-primary)", color: "white" }
                    : { color: "var(--cw-muted)" }
                }
                data-testid={`field-module-${m}`}
              >
                {m}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              data-testid="field-show-inactive"
            />
            Show deactivated fields
          </label>
          <div className="flex-1" />
          <button
            className="cw-btn-primary flex items-center gap-2"
            onClick={() => setShowCreate(true)}
            data-testid="field-create-btn"
          >
            <Plus size={14} /> Create Field
          </button>
        </div>
      </div>

      <div className="cw-card overflow-hidden">
        <table className="w-full text-sm" data-testid="field-library-table">
          <thead className="bg-black/5">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Label</th>
              <th className="text-left px-4 py-2 font-medium">Type</th>
              <th className="text-left px-4 py-2 font-medium">Scope</th>
              <th className="text-left px-4 py-2 font-medium">Used on</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((f) => (
              <tr
                key={f.field_definition_id}
                className={`border-t ${f.is_active === false ? "opacity-60" : ""}`}
                style={{ borderColor: "var(--cw-border)" }}
                data-testid={`field-row-${f.field_key}`}
              >
                <td className="px-4 py-2 font-medium">
                  {f.display_label}
                  {(() => {
                    const a = attachments.find((x) => x.field_definition_id === f.field_definition_id);
                    if (a?.local_label_override) {
                      return (
                        <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                          style={{ background: "rgba(243,191,92,0.20)", color: "#8A6510" }}
                          title={`Renamed locally to "${a.local_label_override}"`}
                          data-testid={`local-override-chip-${f.field_key}`}>
                          renamed locally
                        </span>
                      );
                    }
                    return null;
                  })()}
                </td>
                <td className="px-4 py-2 text-[var(--cw-muted)]">{f.field_type}</td>
                <td className="px-4 py-2 text-[var(--cw-muted)] capitalize">{f.scope_level}</td>
                <td className="px-4 py-2 text-[var(--cw-muted)]">{f.usage_count} records</td>
                <td className="px-4 py-2">
                  {f.is_active === false ? (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(0,0,0,0.08)", color: "var(--cw-muted)" }}>
                      Deactivated
                    </span>
                  ) : f.is_custom ? (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(40,42,116,0.12)", color: "var(--cw-primary)" }}>
                      Custom
                    </span>
                  ) : (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(243,191,92,0.2)", color: "#8A6510" }}>
                      Core
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <div className="flex justify-end gap-1">
                    {/* v5.11 A4 — Rename on every field row */}
                    <button
                      className="p-1.5 rounded hover:bg-black/5"
                      title="Rename"
                      onClick={() => setRenameFor(f)}
                      data-testid={`field-rename-${f.field_key}`}
                    >
                      <Pencil size={14} />
                    </button>
                    {f.is_custom && f.is_active !== false && (
                      <button
                        className="p-1.5 rounded hover:bg-black/5"
                        title="Deactivate"
                        onClick={() => deactivate(f)}
                        data-testid={`field-deactivate-${f.field_key}`}
                      >
                        <PowerOff size={14} />
                      </button>
                    )}
                    {f.is_custom && f.is_active === false && (
                      <button
                        className="p-1.5 rounded hover:bg-black/5"
                        title="Reactivate"
                        onClick={() => reactivate(f)}
                        data-testid={`field-reactivate-${f.field_key}`}
                      >
                        <Power size={14} />
                      </button>
                    )}
                    {f.is_custom && (
                      <button
                        className={`p-1.5 rounded hover:bg-red-50 ${f.usage_count > 0 ? "opacity-30 cursor-not-allowed" : ""}`}
                        title={
                          f.usage_count > 0
                            ? `This field holds values on ${f.usage_count} records and cannot be deleted. Deactivate it instead.`
                            : "Delete permanently"
                        }
                        onClick={() => del(f)}
                        data-testid={`field-delete-${f.field_key}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    {f.is_custom && f.is_active !== false && (
                      isAttached(f.field_definition_id) ? (
                        <button
                          className="p-1.5 rounded hover:bg-black/5"
                          title="Detach from this pipeline"
                          onClick={() => detach(f.field_definition_id)}
                          data-testid={`field-detach-${f.field_key}`}
                        >
                          <Unlink size={14} />
                        </button>
                      ) : (
                        <button
                          className="p-1.5 rounded hover:bg-black/5"
                          title="Attach to this pipeline"
                          onClick={() => attach(f.field_definition_id)}
                          data-testid={`field-attach-${f.field_key}`}
                        >
                          <Link2 size={14} />
                        </button>
                      )
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-sm text-[var(--cw-muted)]">No fields</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateFieldModal
          module={module}
          pipelineId={pipelineId}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {renameFor && (
        <RenameFieldModal
          field={renameFor}
          pipelineId={pipelineId}
          attachmentId={attachmentIdFor(renameFor.field_definition_id)}
          currentAttachedLocalLabel={
            attachments.find((a) => a.field_definition_id === renameFor.field_definition_id)?.local_label_override
          }
          onClose={() => setRenameFor(null)}
          onSaved={() => { setRenameFor(null); load(); }}
        />
      )}
    </div>
  );
}

function CreateFieldModal({ module, pipelineId, onClose, onSaved }) {
  const [label, setLabel] = useState("");
  const [type, setType] = useState("single_line");
  const [scope, setScope] = useState("pipeline");
  const [required, setRequired] = useState(false);
  const [formula, setFormula] = useState("");
  // v5.8 A4: dropdown options editor
  const [options, setOptions] = useState([{ option_label: "" }]);

  const needsOptions = type === "dropdown" || type === "multi_select";

  const submit = async () => {
    if (!label.trim()) { toast.error("Label required"); return; }
    if (type === "computed" && !formula.trim()) { toast.error("Formula required for computed fields"); return; }
    if (needsOptions) {
      const clean = options.filter((o) => (o.option_label || "").trim().length);
      if (clean.length === 0) { toast.error("Add at least one option"); return; }
    }
    try {
      await api.post("/fields", {
        display_label: label.trim(),
        module,
        field_type: type,
        scope_level: scope,
        pipeline_id: scope === "pipeline" ? pipelineId : null,
        is_required: required,
        formula: type === "computed" ? formula.trim() : undefined,
        options: needsOptions
          ? options.filter((o) => (o.option_label || "").trim().length)
              .map((o) => ({ option_label: o.option_label.trim() }))
          : undefined,
      });
      toast.success("Field created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="cw-card p-6 w-full max-w-md" data-testid="field-create-modal">
        <h3 className="cw-h3 mb-4">Create custom field</h3>
        <div className="space-y-3">
          <div>
            <div className="cw-caption">Label</div>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full h-9 px-3 rounded-md border text-sm mt-1"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="field-create-label"
              autoFocus
            />
          </div>
          <div>
            <div className="cw-caption">Type</div>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full h-9 px-3 rounded-md border text-sm mt-1"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="field-create-type"
            >
              {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div className="cw-caption">Scope</div>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full h-9 px-3 rounded-md border text-sm mt-1"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="field-create-scope"
            >
              <option value="pipeline">This pipeline only</option>
              <option value="tenant">Tenant-wide</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
              data-testid="field-create-required"
            />
            Required
          </label>
          {type === "computed" && (
            <div>
              <div className="cw-caption">Formula</div>
              <input
                value={formula}
                onChange={(e) => setFormula(e.target.value)}
                placeholder="e.g. days_since('created_at')"
                className="w-full h-9 px-3 rounded-md border text-sm mt-1 font-mono"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid="field-create-formula"
              />
              <div className="text-[10px] text-[var(--cw-muted)] mt-1">
                Available: <code>days_since('field_key')</code>, arithmetic on
                other field keys. Values are read-only.
              </div>
            </div>
          )}
          {/* v5.8 A4: dropdown/multi-select options editor */}
          {needsOptions && (
            <div data-testid="field-create-options-editor">
              <div className="cw-caption">Options</div>
              <div className="space-y-1 mt-1">
                {options.map((o, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    <input
                      value={o.option_label}
                      placeholder={`Option ${idx + 1}`}
                      onChange={(e) => {
                        const next = [...options];
                        next[idx] = { ...next[idx], option_label: e.target.value };
                        setOptions(next);
                      }}
                      className="flex-1 h-8 px-2 rounded-md border text-xs"
                      style={{ borderColor: "var(--cw-border)" }}
                      data-testid={`field-option-input-${idx}`}
                    />
                    <button
                      className="p-1 rounded hover:bg-red-50"
                      onClick={() => setOptions(options.filter((_, i) => i !== idx))}
                      data-testid={`field-option-remove-${idx}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                <button
                  className="text-xs text-[var(--cw-primary)] flex items-center gap-1 mt-1"
                  onClick={() => setOptions([...options, { option_label: "" }])}
                  data-testid="field-option-add"
                >
                  <Plus size={11} /> Add option
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="cw-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="cw-btn-primary" onClick={submit} data-testid="field-create-submit">
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function RenameFieldModal({ field, pipelineId, attachmentId, currentAttachedLocalLabel, onClose, onSaved }) {
  // v5.11 A4 — Rename with shared/local scope choice.
  //   • "shared"  → PATCH /fields/{id}  updates the field's global display_label (every pipeline sees it)
  //   • "local"   → PATCH /fields/attachments/{id}  writes local_label_override for the current pipeline only
  //     (only available when the field is attached to the current pipeline, which is always true for
  //     custom fields listed here; for core fields there's no attachment so we hide the local option)
  const canLocal = !!attachmentId;
  const [scope, setScope] = useState(canLocal ? "local" : "shared");
  const initial = scope === "local"
    ? (currentAttachedLocalLabel || field.display_label)
    : field.display_label;
  const [label, setLabel] = useState(initial);
  const [busy, setBusy] = useState(false);

  // reset label when the user flips scope
  const onScopeChange = (next) => {
    setScope(next);
    setLabel(next === "local"
      ? (currentAttachedLocalLabel || field.display_label)
      : field.display_label);
  };

  const submit = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      if (scope === "shared") {
        await api.patch(`/fields/${field.field_definition_id}`, { display_label: label.trim() });
        toast.success("Renamed everywhere");
      } else {
        await api.patch(`/fields/attachments/${attachmentId}`, {
          local_label_override: label.trim(),
        });
        toast.success("Renamed in this pipeline only");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally { setBusy(false); }
  };

  const clearLocal = async () => {
    if (!attachmentId) return;
    setBusy(true);
    try {
      await api.patch(`/fields/attachments/${attachmentId}`, { local_label_override: "" });
      toast.success("Reverted to shared name");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      data-testid="rename-field-modal">
      <div className="cw-card p-6 w-full max-w-md">
        <h3 className="cw-h3 mb-1">Rename field</h3>
        <div className="text-xs text-[var(--cw-muted)] mb-4">
          Current name: <strong>{field.display_label}</strong>
          {currentAttachedLocalLabel && (
            <> · Local name in this pipeline: <strong>{currentAttachedLocalLabel}</strong></>
          )}
        </div>

        {/* Scope selector */}
        <div className="space-y-2 mb-4">
          <label className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer ${
            scope === "shared" ? "border-[var(--cw-primary)] bg-[rgba(40,42,116,0.05)]" : ""
          }`} style={{ borderColor: scope === "shared" ? "var(--cw-primary)" : "var(--cw-border)" }}>
            <input type="radio" name="rename-scope" checked={scope === "shared"}
              onChange={() => onScopeChange("shared")}
              data-testid="rename-scope-shared" />
            <div>
              <div className="text-sm font-semibold">Rename everywhere</div>
              <div className="text-xs text-[var(--cw-muted)]">
                Changes the shared name across every pipeline in this tenant.
              </div>
            </div>
          </label>
          <label className={`flex items-start gap-2 p-3 rounded-md border ${
            canLocal ? "cursor-pointer" : "opacity-40 cursor-not-allowed"
          } ${scope === "local" ? "border-[var(--cw-primary)] bg-[rgba(40,42,116,0.05)]" : ""}`}
            style={{ borderColor: scope === "local" ? "var(--cw-primary)" : "var(--cw-border)" }}>
            <input type="radio" name="rename-scope" checked={scope === "local"}
              disabled={!canLocal}
              onChange={() => onScopeChange("local")}
              data-testid="rename-scope-local" />
            <div>
              <div className="text-sm font-semibold">Rename in this pipeline only</div>
              <div className="text-xs text-[var(--cw-muted)]">
                {canLocal
                  ? "Overrides the label for this pipeline. Other pipelines keep the shared name."
                  : "Attach the field to this pipeline first to give it a local name."}
              </div>
            </div>
          </label>
        </div>

        <div className="cw-caption">New label</div>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="w-full h-9 px-3 rounded-md border text-sm mt-1"
          style={{ borderColor: "var(--cw-border)" }}
          autoFocus
          data-testid="rename-field-input"
        />

        <div className="flex justify-between items-center gap-2 mt-5">
          <div>
            {scope === "local" && currentAttachedLocalLabel && (
              <button onClick={clearLocal} disabled={busy}
                className="text-xs text-[var(--cw-error)] hover:underline"
                data-testid="rename-clear-local">
                Revert to shared name
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button className="cw-btn-secondary" onClick={onClose}>Cancel</button>
            <button className="cw-btn-primary" onClick={submit} disabled={busy}
              data-testid="rename-field-save">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
